import { Outlet, Navigate, useNavigate } from "react-router-dom";
import { Spin } from "antd";
import { useUserInfo } from "../contexts/userInfo"; // update path if your file is named differently
import { useSubscriptions } from "../contexts/subscriptionsContext"; // update path if your file is named differently
import { hasFreeTrialExpired } from "../helper/hasFreeTrialExpired";
import { hasFeatureInSubscription } from "../helper/hasFeatureInSubscription";
import FeatureNotAvailableModal from "./FeatureNotAvailable";

const COPARENT_FEATURE =
  "Co-Parenting Calendar (shared child \narrangement tools)";
const CALENDAR_FEATURE = "Personal Event Reminders & Notes (MVP version)";

const FeatureGuard = ({ requiredFeature, requiredFeatures = [], children }) => {
  const { userInfo, loading: userLoading } = useUserInfo();
  const isInvitedMember =
    !Array.isArray(userInfo?.inviter) &&
    userInfo?.inviter?.has_calendar_access == 1;

  // console.log("isInvitedMember", isInvitedMember);

  const {
    purchasedSubscription,
    loading: subsLoading,
    isCoParent,
  } = useSubscriptions();
  const navigate = useNavigate();

  const featuresToCheck = [];
  if (requiredFeature) featuresToCheck.push(requiredFeature);
  if (Array.isArray(requiredFeatures))
    featuresToCheck.push(...requiredFeatures);

  if (userLoading || subsLoading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", marginTop: "20%" }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // 🆕 If user is co-parent, only allow co-parent features
  if (isCoParent && !purchasedSubscription) {
    const hasCoParentAccess = featuresToCheck.includes(COPARENT_FEATURE);

    // If trying to access ANYTHING ELSE -> redirect to pricing
    if (!hasCoParentAccess) {
      return <Navigate to="/pricing" replace />;
    }

    // ✅ Allow co-parenting module
    return children ? children : <Outlet />;
  }

  // 🆕 If user is invited as member, only allow calendar features
  if (isInvitedMember && !purchasedSubscription) {
    const hasCalendarAccess = featuresToCheck.includes(CALENDAR_FEATURE);

    // If trying to access ANYTHING ELSE -> redirect to pricing
    if (!hasCalendarAccess) {
      return <Navigate to="/pricing" replace />;
    }

    // ✅ Allow co-parenting module
    return children ? children : <Outlet />;
  }

  // If user has NOT purchased any subscription
  if (!purchasedSubscription) {
    // If free trial still valid -> allow
    const isTrialActive = !hasFreeTrialExpired(userInfo?.created_at);

    // Notes feature only for trial or subscribed
    const isNotesFeature = featuresToCheck.includes(
      "Shared Notes & Notifications"
    );

    // ✅ Allow trial users to access everything during trial
    if (isTrialActive) return children ? children : <Outlet />;

    // After trial ends
    if (isNotesFeature) {
      // ❌ Trial expired — redirect Notes access
      return <Navigate to="/pricing" replace />;
    }

    // ❌ Trial expired, no subscription -> redirect to pricing
    return <Navigate to="/pricing" replace />;
  }
  const { is_active, billing_cycle, user_subscription_end_date } =
    purchasedSubscription;
  const now = new Date();
  const subscriptionEnd = user_subscription_end_date
    ? new Date(user_subscription_end_date)
    : null;

  let hasAccess = true;

  if (is_active === 1) {
    // Active subscription: OK
    hasAccess = true;
  } else {
    // Cancelled subscription
    if (billing_cycle?.toLowerCase() === "monthly") {
      // Allow until end_date
      hasAccess = subscriptionEnd && now <= subscriptionEnd;
    } else if (
      billing_cycle?.toLowerCase() === "yearly" ||
      billing_cycle?.toLowerCase() === "quarterly"
    ) {
      // End access immediately
      hasAccess = false;
    }
  }

  if (!hasAccess) {
    return <Navigate to="/pricing" replace />;
  }

  const isNotesFeature = featuresToCheck.includes(
    "Shared Notes & Notifications"
  );

  if (isNotesFeature) {
    // Notes are allowed only if subscribed or trial active
    const isTrialActive = !hasFreeTrialExpired(userInfo?.created_at);
    if (isTrialActive || purchasedSubscription) {
      return children ? children : <Outlet />;
    } else {
      return <Navigate to="/pricing" replace />;
    }
  }

  if (featuresToCheck.length > 0) {
    const hasAnyFeature = featuresToCheck.some((feature) => {
      if (feature === COPARENT_FEATURE && isCoParent) {
        return true;
      }
      if (feature === CALENDAR_FEATURE && isInvitedMember) {
        return true;
      }
      return hasFeatureInSubscription(purchasedSubscription, feature);
    });

    // If we get here user has a purchased subscription
    // if (requiredFeature) {
    //   const hasFeature = hasFeatureInSubscription(
    //     purchasedSubscription,
    //     requiredFeature
    //   );
    if (!hasAnyFeature) {
      // Show modal that allows upgrade or go back
      return (
        <FeatureNotAvailableModal
          visible={true}
          onCancel={() => navigate(-1)}
          onUpgrade={() => navigate("/pricing")}
          title="Feature not available"
          message={
            "This feature is not included in your current subscription. Upgrade to access it."
          }
        />
      );
    }
  }

  // Default: allow access
  return children ? children : <Outlet />;
};

export default FeatureGuard;
